<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { iconMap } from "@core/assets/icons/iconMap.js";
	import type { SubscriptionParams } from "@core/schema/Subscription.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { subscriptionKindNameMap } from "@core/schema/names/subscriptionKindNameMap.js";
	import { subscriptionSelectionNameMap } from "@core/schema/names/subscriptionSelectionNameMap.js";
	import { subscriptionSizeNameMap } from "@core/schema/names/subscriptionSizeNameMap.js";
	import { subscriptionVariantNameMap } from "@core/schema/names/subscriptionVariantNameMap.js";
	import { membershipPricePerCurrencyMap } from "@core/schema/utils/membershipPricePerCurrencyMap.js";
	import { subscriptionVariantIconMap } from "@core/schema/utils/subscriptionVariantIconMap.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CartSummarySubscription.translations.json" with { type: "json" };

	const { subscription }: { subscription: SubscriptionParams } = $props();

	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();

	const icon = $derived(subscriptionVariantIconMap[subscription.variant]);
	const isRecurring = $derived(subscription.kind === SubscriptionKind.Recurring);

	const membershipPrice = $derived(
		formatPrice(
			membershipPricePerCurrencyMap[Cart.currency ?? tenant.defaultCurrency],
			Cart.currency ?? tenant.defaultCurrency,
			tenant.locale,
			false,
		),
	);

	const price = $derived(
		formatPrice(Cart.subscriptionPrice.withTax, Cart.currency ?? tenant.defaultCurrency, tenant.locale, false),
	);
</script>

<div class="flex p-6 gap-4 bg-fcrlightgray-200 border border-fcrlightgray-300 rounded-3xl mb-1">
	<div>
		{#if typeof icon === "string"}
			<Icon {icon} class="size-6 text-fcrdarkbrown" />
		{:else if typeof icon === "object"}
			{@const resolvedIcon = iconMap[icon.key]}
			{#if resolvedIcon}
				<Icon icon={resolvedIcon} style="color: {icon.color};" class="size-6" />
			{/if}
		{/if}
	</div>
	<div class="flex flex-col gap-1 font-medium">
		<div class="text-lg/6 font-antipol">
			<Formatted text={subscriptionKindNameMap[subscription.kind][tenant.locale]} />
		</div>
		<div class=" text-fcrgray mb-3">
			<Formatted
				text={[
					subscriptionVariantNameMap[subscription.variant][tenant.locale],
					subscriptionSelectionNameMap[subscription.selection][tenant.locale],
					`${translate(`size${subscription.size}`)} (${subscriptionSizeNameMap[subscription.size][tenant.locale]})`,
				].join(" • ")}
			/>
		</div>
		<div class="font-antipol">
			{#if isRecurring}
				<Formatted text={translate("monthly", { price })} />
			{:else}
				{price}
			{/if}
		</div>
		{#if isRecurring}
			<div class="text-fcrgray text-sm/normal mt-1">
				<Formatted text={translate("membershipIncluded", { price: membershipPrice })} />
			</div>
		{/if}
	</div>
</div>
